// ABOUTME: SPEC-62 — how much of the collection piece card each part gets, at a given viewport.
// ABOUTME: One reason to change: the mobile card's height ignored viewport HEIGHT (starved/erased it
// ABOUTME: on a short window) and both compositions gave the product image under half the card, even
// ABOUTME: though the owner ruled the image is the subject and the description/spec text may scroll.

import {
  ICON_RAIL_MAX_BOTTOM, RAIL_TOUCH_OVERSHOOT_GUARD, mobileUnitFor, viewportHeightLess,
} from '../ReelChrome/mobileGeometry';
import { railUnitFor } from '../ReelChrome/railGeometry';

/**
 * Tunables, each with the reason it has this value (SPEC-62 §3). Never a literal in the JSX.
 */
export const PIECE_CARD_LAYOUT = {
  mobileImageShare: 0.58,      // D-5 — was '42%', of the card's inner height; also the stacked
                                // desktop split's `gridTemplateRows` first term
  desktopImageFr: 1.45,        // D-5 — wide desktop grid becomes `1.45fr 1fr`
  minCardHeightPx: 240,        // D-3 — image floor 140 + details floor 76 + padding ~24
  thumbsMinGalleryPx: 160,     // D-4 — strip 40 + gap 8, leaving the image >= 110 (2.75 strips)
  detailsMinWidthPx: 200,      // D-6 — heart+bag 78.9 + ~120 for one legible title line
  backButtonGutterGap: 20,     // D-7 — authored px, one `u(20)` off the gutter cap
};

// Mobile card's own authored offsets (CollectionPieceCard.js's MOBILE_CARD). Derived from the same
// exports MOBILE_CARD.top already composes, rather than a copied 446.667, so the two can never drift
// apart (mobileGeometry's own ICON_RAIL_MAX_BOTTOM/RAIL_TOUCH_OVERSHOOT_GUARD comments have the why).
const MOBILE_TOP_AUTHORED = ICON_RAIL_MAX_BOTTOM + RAIL_TOUCH_OVERSHOOT_GUARD; // 446.667
// The CC-bar gutter MOBILE_CARD.bottom (CollectionPieceCard.js) authors as 313 — duplicated here
// rather than imported because CollectionPieceCard.js imports FROM this module (D-8); a shared
// constant would cycle the two files. Retune one, retune both.
const MOBILE_BOTTOM_GUTTER_AUTHORED = 313;
const MOBILE_PAD_AUTHORED = 45; // MOBILE_CARD.pad

// Desktop card box (CollectionPieceCard.js's CARD_BOX, Figma 403:1448), as fractions/authored px.
// Same duplication trade as above, for the same reason (no import cycle back into the component).
const DESKTOP_CARD_HEIGHT_FRACTION = 1333 / 2160; // CARD_BOX.heightPct / 100
const DESKTOP_CARD_WIDTH_FRACTION = 2666 / 3840;  // CARD_BOX.widthPct / 100
const DESKTOP_CARD_PAD_AUTHORED = 53.333;         // CARD_BOX.pad

// Safe default for an unmeasured viewport (SSR, first jsdom paint) — the same "roomy" branch
// `resolveComposition` takes so nothing flashes a degraded card before the first real measurement.
const UNMEASURED_LAYOUT = {
  cardHeightPx: 0,
  galleryHeightPx: 0,
  showThumbs: true,
  stacked: false,
  gridTemplateColumns: `${PIECE_CARD_LAYOUT.desktopImageFr}fr 1fr`,
  imageSharePct: PIECE_CARD_LAYOUT.mobileImageShare * 100,
};

/**
 * D-1's two-term expression, as CSS: where the room below `top` allows it, the card renders exactly
 * where today's `bottom: 313` gutter puts it; where it does not, the authored gutter yields first
 * (D-2) and `minCardHeightPx` stops the card vanishing (D-3). Mirrors `railGeometry.js`'s
 * `panelMaxHeight()` / `panelMaxHeightFor()` pair — a CSS string here, the same decision as a number
 * in `pieceCardLayoutFor` below; a pinning test keeps the two in step (AC-1).
 */
export function mobileCardHeight(liftAuthored = 0) {
  const roomBelowTop = viewportHeightLess(MOBILE_TOP_AUTHORED);
  // SPEC-82 control_reveal: the gutter grows by the revealed row's lift so the card's bottom clears the row
  // (the Vesper sheet rises by the same CONTROLS_LIFT); 0 when the row is closed, so the string is unchanged.
  const roomAtTodaysGutter = viewportHeightLess(MOBILE_TOP_AUTHORED + MOBILE_BOTTOM_GUTTER_AUTHORED + liftAuthored);
  return `max(0px, min(${roomBelowTop}, max(${PIECE_CARD_LAYOUT.minCardHeightPx}px, ${roomAtTodaysGutter})))`;
}

/**
 * The same decisions as numbers, for the components (which part shows) and the tests (which numbers
 * pin the behaviour) — pure, no DOM, no I/O; never throws.
 *
 * @param {{width: number, height: number}} viewport
 * @param {'desktop'|'mobile'} composition
 * @param {number} [liftAuthored] the revealed control row's lift (mobile authored px), 0 when closed
 * @returns {{cardHeightPx: number, galleryHeightPx: number, showThumbs: boolean, stacked: boolean,
 *            gridTemplateColumns: string, imageSharePct: number}}
 */
export function pieceCardLayoutFor(viewport, composition, liftAuthored = 0) {
  const width = viewport?.width || 0;
  const height = viewport?.height || 0;
  if (!width || !height) return UNMEASURED_LAYOUT;

  if (composition === 'mobile') {
    const u = mobileUnitFor(width);
    const roomBelowTop = height - MOBILE_TOP_AUTHORED * u;
    const cardHeightPx = Math.max(0, Math.min(
      roomBelowTop,
      Math.max(PIECE_CARD_LAYOUT.minCardHeightPx, roomBelowTop - (MOBILE_BOTTOM_GUTTER_AUTHORED + liftAuthored) * u),
    ));
    const galleryHeightPx = (cardHeightPx - 2 * MOBILE_PAD_AUTHORED * u) * PIECE_CARD_LAYOUT.mobileImageShare;
    return {
      cardHeightPx,
      galleryHeightPx,
      showThumbs: galleryHeightPx >= PIECE_CARD_LAYOUT.thumbsMinGalleryPx,
      stacked: false,
      gridTemplateColumns: null,
      imageSharePct: PIECE_CARD_LAYOUT.mobileImageShare * 100,
    };
  }

  const u = railUnitFor(width);
  const cardHeightPx = height * DESKTOP_CARD_HEIGHT_FRACTION;
  const cardWidthPx = width * DESKTOP_CARD_WIDTH_FRACTION;
  const detailsInner = cardWidthPx / (1 + PIECE_CARD_LAYOUT.desktopImageFr) - 2 * DESKTOP_CARD_PAD_AUTHORED * u;
  const stacked = detailsInner < PIECE_CARD_LAYOUT.detailsMinWidthPx;
  const galleryInnerPx = cardHeightPx - 2 * DESKTOP_CARD_PAD_AUTHORED * u;
  const galleryHeightPx = stacked ? galleryInnerPx * PIECE_CARD_LAYOUT.mobileImageShare : galleryInnerPx;
  return {
    cardHeightPx,
    galleryHeightPx,
    showThumbs: galleryHeightPx >= PIECE_CARD_LAYOUT.thumbsMinGalleryPx,
    stacked,
    gridTemplateColumns: stacked ? '1fr' : `${PIECE_CARD_LAYOUT.desktopImageFr}fr 1fr`,
    imageSharePct: PIECE_CARD_LAYOUT.mobileImageShare * 100,
  };
}
